// ============================================================================
// client/victoire-fx.ts — LES CÉLÉBRATIONS DE LA VAGUE DU 30/07
// ----------------------------------------------------------------------------
// Trois effets de fin de niveau, sortis de client/solo.ts pour une raison
// précise : la planche de validation /pages/cosmetiques.html doit pouvoir les
// peindre elle aussi, et importer solo.ts tirerait tout le jeu derrière.
//
// Module PUR : ni DOM, ni simulation, ni Date.now, ni Math.random. Il reçoit un
// contexte, deux dimensions et un âge en millisecondes. Rien ici n'entre dans le
// hash d'état.
// ============================================================================

import { ENCRE, OMBRE_TON, rgba } from './papier'

type Ctx = CanvasRenderingContext2D

/* ═════════════════════════════════════════════════════ 3. LES VICTOIRES ═══ */
/* Même contrat que drawVictoryFx de client/solo.ts : on peint par-dessus l'écran
   de fin, en fonction de l'âge de la victoire en millisecondes. Le contexte est
   déjà save()é par l'appelant.
   ⚠️ AUCUN Math.random ici : deux joueurs qui finissent le même niveau doivent
   voir la même chose, et un effet qui scintille au hasard clignote à chaque
   image. Le désordre vient d'un HACHAGE de l'indice, donc stable. */
const melisse = (i: number, sel: number): number => {
  let h = ((i + 1) * 73856093) ^ ((sel + 1) * 19349663)
  h = (h ^ (h >>> 13)) >>> 0
  return (h % 1000) / 1000
}

/* ⚠️ TOUT EST EN PROPORTION DE L'ÉCRAN, RIEN EN PIXELS. Première version écrite
   en pixels fixes : sur la planche (360 × 210) elle paraissait correcte, alors
   que le canvas du jeu fait 1344 × 816 — presque quatre fois la diagonale. Les
   papiers sortaient du cadre de la planche et auraient été minuscules en jeu.
   `U` vaut 1 % de la diagonale : un effet réglé en U se comporte pareil sur la
   planche, sur le Mac et sur le téléphone des enfants. */
const unite = (W: number, H: number): number => Math.hypot(W, H) / 100

export function victoireFx(ctx: Ctx, W: number, H: number, ageMs: number, kind: string): boolean {
  const U = unite(W, H)
  if (kind === 'victoire-fanions') {
    /* GUIRLANDE DE FANIONS — courant. Une corde qui traverse l'écran et des
       fanions qui tombent l'un après l'autre, de gauche à droite. Le geste est
       lent et lisible : c'est l'effet d'entrée de gamme, il ne doit pas voler la
       vedette aux mégas. */
    const N = 15, y0 = H * 0.07, creux = H * 0.09
    const corde = (x: number): number => y0 + Math.sin((x / W) * Math.PI) * creux
    ctx.strokeStyle = '#6b5a3e'; ctx.lineWidth = U * 0.16
    ctx.beginPath()
    for (let x = 0; x <= W; x += W / 60) x ? ctx.lineTo(x, corde(x)) : ctx.moveTo(x, corde(x))
    ctx.stroke()
    const COLS = ['#d8342e', '#ffd23f', '#3a9ad4', '#5fbf4f', '#e08a3a']
    const demi = (W / N) * 0.42, L0 = H * 0.13
    for (let i = 0; i < N; i++) {
      const t = ageMs - i * 70
      if (t < 0) continue
      const k = Math.min(1, t / 420)
      const x = ((i + .5) / N) * W, yy = corde(x)
      const bal = Math.sin(t / 320 + i) * (1 - k * .75) * .5      // le fanion se balance puis se pose
      const L = L0 * (0.35 + 0.65 * k)
      ctx.save(); ctx.translate(x, yy); ctx.rotate(bal)
      ctx.fillStyle = rgba(OMBRE_TON, .25)
      ctx.beginPath(); ctx.moveTo(-demi * .9, U * .2); ctx.lineTo(demi * .9, U * .2)
      ctx.lineTo(U * .2, L + U * .2); ctx.closePath(); ctx.fill()
      ctx.fillStyle = COLS[i % COLS.length]!
      ctx.beginPath(); ctx.moveTo(-demi, 0); ctx.lineTo(demi * .85, 0); ctx.lineTo(0, L)
      ctx.closePath(); ctx.fill()
      ctx.restore()
    }
    return true
  }

  if (kind === 'victoire-ballons') {
    /* ENVOLÉE DE BALLONS — rare. Ils montent depuis le bas, se croisent, et
       sortent par le haut. Le ballon est peint en deux tons (le corps et un
       reflet) : sans le reflet, à cette taille, c'est un rond de couleur. */
    const N = 11
    const COLS: [string, string][] = [
      ['#d8342e', '#ff8a80'], ['#ffd23f', '#fff0a8'], ['#3a9ad4', '#8fd0f0'],
      ['#5fbf4f', '#a8e89a'], ['#b06ae0', '#dcb0f4'],
    ]
    for (let i = 0; i < N; i++) {
      const t = ageMs - i * 130
      if (t < 0) continue
      const k = t / 2600
      if (k > 1.25) continue
      const x = (0.06 + melisse(i, 1) * 0.88) * W + Math.sin(t / 620 + i) * U * 1.4
      const y = H * 1.12 - k * H * 1.35
      const r = U * (1.7 + melisse(i, 2) * 1.1)
      const [corps, reflet] = COLS[i % COLS.length]!
      ctx.globalAlpha = Math.max(0, Math.min(1, (1.25 - k) * 2.5))
      ctx.strokeStyle = rgba(ENCRE, .45); ctx.lineWidth = U * 0.08   // la ficelle, qui ondule
      ctx.beginPath(); ctx.moveTo(x, y + r)
      ctx.quadraticCurveTo(x + Math.sin(t / 400 + i) * r * .6, y + r * 2.1, x, y + r * 3.1); ctx.stroke()
      ctx.fillStyle = corps
      ctx.beginPath(); ctx.ellipse(x, y, r * .86, r, 0, 0, 7); ctx.fill()
      ctx.beginPath(); ctx.moveTo(x - r * .22, y + r); ctx.lineTo(x + r * .22, y + r)
      ctx.lineTo(x, y + r * 1.3); ctx.closePath(); ctx.fill()    // le nœud
      ctx.fillStyle = reflet                                     // le reflet, en haut à gauche
      ctx.beginPath(); ctx.ellipse(x - r * .3, y - r * .35, r * .24, r * .32, -.4, 0, 7); ctx.fill()
    }
    ctx.globalAlpha = 1
    return true
  }

  if (kind === 'victoire-lanternes') {
    /* ENVOL DE LANTERNES — épique (vague du 05/08, planche cosmetiques2). Des
       lanternes de papier montent dans la nuit en se balançant, chacune porte
       sa flamme : un cœur clair au centre, pas un dégradé — le jeu peint en
       aplats. Elles montent LENTEMENT : une lanterne pressée est un ballon. */
    const N = 9
    for (let i = 0; i < N; i++) {
      const t = ageMs - i * 210
      if (t < 0) continue
      const k = t / 3400
      if (k > 1.2) continue
      const x = (0.08 + melisse(i, 11) * 0.84) * W + Math.sin(t / 900 + i * 2.1) * U * 2.2
      const y = H * 1.1 - k * H * 1.28
      const s = U * (2.1 + melisse(i, 12) * 1.1)          // la demi-largeur du corps
      const h = s * 1.5                                   // la demi-hauteur
      ctx.save()
      ctx.globalAlpha = Math.max(0, Math.min(1, (1.2 - k) * 3))
      ctx.translate(x, y); ctx.rotate(Math.sin(t / 760 + i) * .07)
      ctx.fillStyle = rgba('#ffbe6e', .16)                // le halo, peint d'abord
      ctx.beginPath(); ctx.arc(0, 0, s * 2.6, 0, 7); ctx.fill()
      ctx.fillStyle = '#e8923a'                           // le corps
      ctx.beginPath()
      ctx.moveTo(-s * .72, -h); ctx.lineTo(s * .72, -h)
      ctx.quadraticCurveTo(s * 1.12, 0, s * .66, h)
      ctx.lineTo(-s * .66, h)
      ctx.quadraticCurveTo(-s * 1.12, 0, -s * .72, -h)
      ctx.closePath(); ctx.fill()
      ctx.fillStyle = '#b86a24'                           // colerettes haut et bas
      ctx.fillRect(-s * .72, -h - U * .28, s * 1.44, U * .42)
      ctx.fillRect(-s * .66, h - U * .14, s * 1.32, U * .42)
      ctx.strokeStyle = rgba('#8a4c16', .5); ctx.lineWidth = U * .1   // les nervures du papier
      for (const dx of [-.4, 0, .4]) {
        ctx.beginPath(); ctx.moveTo(dx * s, -h + U * .2); ctx.lineTo(dx * s * 1.35, h - U * .2); ctx.stroke()
      }
      ctx.fillStyle = '#ffe4b0'                           // la flamme, par transparence
      ctx.beginPath(); ctx.ellipse(0, h * .2, s * .34, s * .5, 0, 0, 7); ctx.fill()
      ctx.restore()
    }
    ctx.globalAlpha = 1
    return true
  }

  if (kind === 'victoire-aurore') {
    /* AURORE BORÉALE — méga (vague du 05/08). Trois RUBANS DE PAPIER ondulent
       en haut de l'écran, vert puis turquoise puis violet, chacun bordé d'un
       liseré clair : la version papier découpé d'une aurore, pas un dégradé.
       Elle ne finit jamais — l'écran de fin reste sous son ciel. */
    const entree = Math.min(1, ageMs / 900)
    const RUBANS: [string, number, number, number, number][] = [
      ['#5fe89a', .30, H * .16, H * .055, 0],             // [couleur, alpha, base, amplitude, phase]
      ['#6ad8c8', .24, H * .24, H * .07, 2.1],
      ['#b06ae0', .20, H * .33, H * .085, 4.3],
    ]
    for (const [col, al, base, amp, ph] of RUBANS) {
      const bord = (x: number): number =>
        base + Math.sin(x / W * 4.4 + ageMs / 1300 + ph) * amp
              + Math.sin(x / W * 9.2 - ageMs / 2100 + ph * 1.7) * amp * .45
      ctx.globalAlpha = al * entree
      ctx.fillStyle = col
      ctx.beginPath(); ctx.moveTo(0, 0)
      for (let x = 0; x <= W; x += W / 48) ctx.lineTo(x, bord(x))
      ctx.lineTo(W, 0); ctx.closePath(); ctx.fill()
      ctx.globalAlpha = al * 1.6 * entree                 // le liseré, bord de papier
      ctx.strokeStyle = '#eafff2'; ctx.lineWidth = U * .16
      ctx.beginPath()
      for (let x = 0; x <= W; x += W / 48) x ? ctx.lineTo(x, bord(x)) : ctx.moveTo(x, bord(x))
      ctx.stroke()
    }
    for (let i = 0; i < 14; i++) {                        // les étoiles, qui respirent
      const sx = melisse(i, 21) * W, sy = melisse(i, 22) * H * .38
      const puls = .35 + .65 * Math.abs(Math.sin(ageMs / 1000 + i * 1.7))
      ctx.globalAlpha = puls * .8 * entree
      ctx.fillStyle = '#fff8e0'
      const r = U * (.14 + melisse(i, 23) * .16)
      ctx.fillRect(sx - r, sy - r * .3, r * 2, r * .6)
      ctx.fillRect(sx - r * .3, sy - r, r * .6, r * 2)
    }
    ctx.globalAlpha = 1
    return true
  }

  if (kind === 'victoire-papiers') {
    /* ENVOL DE PAPIERS — méga, et le seul effet qui parle du JEU lui-même :
       depuis la refonte, tout Mad'Tabornio est en papier découpé. Ici l'écran
       part en morceaux de papier — ronds, triangles, bandes — qui s'envolent en
       tournant. La palette est celle de la matière (client/papier.ts), pas une
       palette de fête : c'est ce qui le rattache au reste du jeu au lieu d'en
       faire un feu d'artifice de plus. */
    const N = 64
    const COLS = ['#f2e4cc', '#ffd23f', '#d8342e', '#3a9ad4', '#c08a3a', '#5fbf4f', '#f7f2e6']
    const cx = W / 2, cy = H * 0.46
    for (let i = 0; i < N; i++) {
      const t = ageMs - melisse(i, 7) * 260
      if (t < 0) continue
      const k = t / 2200
      if (k > 1) continue
      const a = melisse(i, 3) * Math.PI * 2
      const vit = U * (12 + melisse(i, 4) * 22)
      const x = cx + Math.cos(a) * vit * k + Math.sin(t / 300 + i) * U * 1.6 * k
      const y = cy + Math.sin(a) * vit * k * .72 + k * k * H * .38 - k * H * .19  // gerbe puis retombée
      const s = U * (0.6 + melisse(i, 5) * 0.9)
      const rot = melisse(i, 6) * 7 + t / 260
      ctx.save()
      ctx.globalAlpha = Math.max(0, Math.min(1, (1 - k) * 2))
      ctx.translate(x, y); ctx.rotate(rot)
      ctx.scale(1, Math.abs(Math.cos(t / 340 + i)) * .85 + .15)            // la feuille se retourne
      ctx.fillStyle = rgba(OMBRE_TON, .22)
      ctx.fillRect(-s / 2 + s * .16, -s / 2 + s * .22, s, s * .72)
      ctx.fillStyle = COLS[i % COLS.length]!
      const forme = i % 3
      if (forme === 0) ctx.fillRect(-s / 2, -s / 2, s, s * .72)
      else if (forme === 1) {
        ctx.beginPath(); ctx.moveTo(-s / 2, s / 2); ctx.lineTo(0, -s / 2); ctx.lineTo(s / 2, s / 2)
        ctx.closePath(); ctx.fill()
      } else { ctx.beginPath(); ctx.arc(0, 0, s * .45, 0, 7); ctx.fill() }
      ctx.restore()
    }
    ctx.globalAlpha = 1
    return true
  }
  return false
}
